import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, GraduationCap, ClipboardList, Receipt, Tags,
  FolderOpen, CalendarDays, Users, Settings as SettingsIcon, DownloadCloud, KeyRound,
  LogOut, Menu, X, HeartHandshake, Eye, Sparkles,
} from "lucide-react";
import Logo from "@/components/Logo";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { BillingDot } from "@/components/BillingStatus";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const groups = [
  {
    label: null,
    items: [
      { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", end: true },
      { to: "/appointments", icon: CalendarDays, label: "Appointments", testid: "nav-appointments" },
    ],
  },
  {
    label: "Memberships",
    items: [
      { to: "/courses", icon: GraduationCap, label: "Courses", testid: "nav-courses" },
      { to: "/quizzes", icon: ClipboardList, label: "Quizzes & Surveys", testid: "nav-quizzes" },
      { to: "/resources", icon: FolderOpen, label: "Resources", testid: "nav-resources" },
      { to: "/portal-content", icon: Sparkles, label: "What's New & Discover", testid: "nav-portal-extras" },
    ],
  },
  {
    label: "Finances",
    items: [
      { to: "/plans", icon: Tags, label: "Pricing & Plans", testid: "nav-plans" },
      { to: "/invoices", icon: Receipt, label: "Invoices", testid: "nav-invoices" },
    ],
  },
  {
    label: "Business & Operations",
    items: [
      { to: "/clients", icon: Users, label: "Clients", testid: "nav-clients" },
    ],
  },
  {
    label: "Settings",
    items: [
      { to: "/import", icon: DownloadCloud, label: "Import from GHL", testid: "nav-import" },
      { to: "/sso-setup", icon: KeyRound, label: "Client Portal SSO", testid: "nav-sso" },
      { to: "/settings", icon: SettingsIcon, label: "GHL Connection", testid: "nav-settings" },
    ],
  },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const previewPortal = async () => {
    try {
      await api.post("/portal/preview");
      window.open("/portal", "_blank", "noopener");
    } catch (e) {
      toast.error("Couldn't open the client preview. Please try again.");
    }
  };
  return (
    <div className="flex h-full flex-col bg-white border-r border-navy-900/5">
      <div className="px-5 py-7">
        <Logo size="h-14" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-4 space-y-6">
        {groups.map((group, gi) => (
          <div key={group.label || "primary"}>
            {group.label && (
              <div className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-400">
                {group.label}
              </div>
            )}
            <div className="space-y-1">
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                      isActive
                        ? "bg-gold-50 text-gold-700"
                        : "text-navy-500 hover:bg-navy-50 hover:text-navy-900"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon
                        className={`h-[18px] w-[18px] ${isActive ? "text-gold-600" : "text-navy-400 group-hover:text-navy-700"}`}
                        strokeWidth={2}
                      />
                      {item.label}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="border-t border-navy-900/5 p-4">
        <div className="mb-3 rounded-xl bg-gold-50 p-4">
          <div className="flex items-center gap-2 text-navy-900">
            <HeartHandshake className="h-4 w-4 text-gold-600" />
            <span className="text-sm font-semibold">Signed in</span>
          </div>
          <p className="mt-1 truncate text-xs text-navy-500">{user?.email}</p>
          <BillingDot />
          <ChangePasswordDialog />
        </div>
        <Button
          variant="outline"
          data-testid="preview-portal-button"
          onClick={previewPortal}
          className="mb-2 w-full justify-start gap-3 rounded-xl border-gold-200 text-gold-700 hover:bg-gold-50 hover:text-gold-800"
        >
          <Eye className="h-[18px] w-[18px]" />
          Preview client portal
        </Button>
        <Button
          variant="ghost"
          data-testid="logout-button"
          onClick={logout}
          className="w-full justify-start gap-3 text-navy-500 hover:text-navy-900 hover:bg-navy-50"
        >
          <LogOut className="h-[18px] w-[18px]" />
          Sign out
        </Button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-ivory">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-navy-900/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 animate-fade-up">
            <SidebarContent onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <div className="lg:pl-72">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-20 flex items-center justify-between border-b border-navy-900/5 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
          <Logo size="h-9" />
          <Button variant="ghost" size="icon" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        <main key={location.pathname} className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-10 animate-fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
